export const WEFAT_THEME_API_NAMES = Object.freeze([
    'onThemeChange',
    'offThemeChange',
    'setNavigationBarColor',
    'setNavigationBarTitle',
    'setBackgroundColor',
]);
export class WxThemeRuntimeError extends Error {
    code;
    errCode;
    errMsg;
    api;
    remediation;
    constructor(code, api, reason, remediation, cause) {
        const errMsg = `${api}:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxThemeRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.api = api;
        this.remediation = remediation;
        if (cause !== undefined) {
            Object.defineProperty(this, 'cause', { value: cause });
        }
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
function optionError(api, reason) {
    return new WxThemeRuntimeError('WF6141', api, reason, 'Pass documented theme or navigation-title options with valid values and callable callbacks.');
}
function environmentError(api, reason, cause) {
    return new WxThemeRuntimeError('WF6140', api, reason, 'Run the theme adapter in a browser document with matchMedia support or inject a complete environment.', cause);
}
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function readOptions(api, raw, allowed) {
    if (!isPlainRecord(raw)) {
        throw optionError(api, 'options must be a plain object');
    }
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !allowed.has(key)) {
            throw optionError(api, `unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(api, `option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(api, `${key} must be a function when provided`);
        }
    }
    return raw;
}
function callbacks(options) {
    return {
        ...(typeof options.success === 'function' ? { success: options.success } : {}),
        ...(typeof options.fail === 'function' ? { fail: options.fail } : {}),
        ...(typeof options.complete === 'function'
            ? { complete: options.complete }
            : {}),
    };
}
function color(api, value, field, required) {
    if (value === undefined && !required)
        return undefined;
    if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
        throw optionError(api, `${field} must be a six-digit hex color`);
    }
    return value.toLowerCase();
}
function invokeCallback(callback, value, api, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, api, kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
function runMutation(api, callbackSet, mutation, report) {
    const task = Promise.resolve().then(() => {
        try {
            mutation();
            return Object.freeze({ errMsg: `${api}:ok` });
        }
        catch (error) {
            throw error instanceof WxThemeRuntimeError
                ? error
                : environmentError(api, 'browser theme mutation failed', error);
        }
    });
    if (callbackSet.success === undefined && callbackSet.fail === undefined && callbackSet.complete === undefined) {
        return task;
    }
    void task.then((result) => {
        invokeCallback(callbackSet.success, result, api, 'success', report);
        invokeCallback(callbackSet.complete, result, api, 'complete', report);
    }, (error) => {
        invokeCallback(callbackSet.fail, error, api, 'fail', report);
        invokeCallback(callbackSet.complete, error, api, 'complete', report);
    });
    return undefined;
}
function browserEnvironment() {
    if (typeof window === 'undefined' || typeof document === 'undefined' || typeof window.matchMedia !== 'function') {
        return null;
    }
    return {
        media: window.matchMedia('(prefers-color-scheme: dark)'),
        applyNavigationBarColor(backgroundColor, frontColor) {
            let meta = document.querySelector('meta[name="theme-color"]');
            if (meta === null) {
                meta = document.createElement('meta');
                meta.setAttribute('name', 'theme-color');
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', backgroundColor);
            document.documentElement.style.setProperty('--wefat-navigation-background-color', backgroundColor);
            document.documentElement.style.setProperty('--wefat-navigation-front-color', frontColor);
        },
        applyNavigationBarTitle(title) {
            document.title = title;
        },
        applyBackgroundColor(backgroundColor, backgroundColorTop, backgroundColorBottom) {
            document.documentElement.style.backgroundColor = backgroundColorTop;
            document.documentElement.style.setProperty('--wefat-background-bottom-color', backgroundColorBottom);
            if (document.body !== null)
                document.body.style.backgroundColor = backgroundColor;
        },
    };
}
export function createBrowserThemeWxApis(options = {}) {
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    const candidate = options.environment ?? browserEnvironment();
    if (candidate === null || candidate.media === null || typeof candidate.media !== 'object') {
        throw new TypeError('createBrowserThemeWxApis requires a browser theme environment');
    }
    const environment = candidate;
    const listeners = new Map();
    function onThemeChange(listener) {
        if (typeof listener !== 'function')
            throw optionError('onThemeChange', 'listener must be a function');
        if (listeners.has(listener))
            return;
        const browserListener = (event) => listener(Object.freeze({
            theme: event.matches ? 'dark' : 'light',
        }));
        try {
            if (environment.media.addEventListener !== undefined) {
                environment.media.addEventListener('change', browserListener);
            }
            else if (environment.media.addListener !== undefined) {
                environment.media.addListener(browserListener);
            }
            else {
                throw environmentError('onThemeChange', 'media query listeners are unavailable');
            }
            listeners.set(listener, browserListener);
        }
        catch (error) {
            throw error instanceof WxThemeRuntimeError
                ? error
                : environmentError('onThemeChange', 'could not register the media query listener', error);
        }
    }
    function offThemeChange(listener) {
        if (listener !== undefined && typeof listener !== 'function') {
            throw optionError('offThemeChange', 'listener must be a function when provided');
        }
        const entries = listener === undefined
            ? [...listeners]
            : listeners.has(listener)
                ? [[listener, listeners.get(listener)]]
                : [];
        for (const [registered, browserListener] of entries) {
            if (environment.media.removeEventListener !== undefined) {
                environment.media.removeEventListener('change', browserListener);
            }
            else {
                environment.media.removeListener?.(browserListener);
            }
            listeners.delete(registered);
        }
    }
    return Object.freeze({
        onThemeChange,
        offThemeChange,
        setNavigationBarColor(rawOptions) {
            const api = 'setNavigationBarColor';
            const values = readOptions(api, rawOptions, new Set(['backgroundColor', 'frontColor', ...CALLBACK_KEYS]));
            const backgroundColor = color(api, values.backgroundColor, 'backgroundColor', true);
            const frontColor = color(api, values.frontColor, 'frontColor', true);
            if (frontColor !== '#000000' && frontColor !== '#ffffff') {
                throw optionError(api, 'frontColor must be #000000 or #ffffff');
            }
            return runMutation(api, callbacks(values), () => {
                environment.applyNavigationBarColor(backgroundColor, frontColor);
            }, options.onCallbackError);
        },
        setNavigationBarTitle(rawOptions) {
            const api = 'setNavigationBarTitle';
            const values = readOptions(api, rawOptions, new Set(['title', ...CALLBACK_KEYS]));
            if (typeof values.title !== 'string') {
                throw optionError(api, 'title must be a string');
            }
            const title = values.title;
            return runMutation(api, callbacks(values), () => {
                environment.applyNavigationBarTitle(title);
            }, options.onCallbackError);
        },
        setBackgroundColor(rawOptions) {
            const api = 'setBackgroundColor';
            const values = readOptions(api, rawOptions, new Set([
                'backgroundColor', 'backgroundColorTop', 'backgroundColorBottom', ...CALLBACK_KEYS,
            ]));
            const backgroundColor = color(api, values.backgroundColor, 'backgroundColor', false);
            const backgroundColorTop = color(api, values.backgroundColorTop, 'backgroundColorTop', false);
            const backgroundColorBottom = color(api, values.backgroundColorBottom, 'backgroundColorBottom', false);
            if (backgroundColor === undefined && backgroundColorTop === undefined && backgroundColorBottom === undefined) {
                throw optionError(api, 'at least one background color must be provided');
            }
            const center = backgroundColor ?? backgroundColorTop ?? backgroundColorBottom;
            return runMutation(api, callbacks(values), () => {
                environment.applyBackgroundColor(center, backgroundColorTop ?? center, backgroundColorBottom ?? center);
            }, options.onCallbackError);
        },
    });
}
//# sourceMappingURL=theme-adapter.js.map